import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { usePushNotifications } from './hooks/usePushNotifications';
import Layout from './components/Layout';
import Login from './pages/Login';
import Register from './pages/Register';
import Home from './pages/Home';
import Watch from './pages/Watch';
import Admin from './pages/Admin';
import Teams from './pages/Teams';
import TeamPage from './pages/TeamPage';
import PlayerProfile from './pages/PlayerProfile';
import Terms from './pages/Terms';
import Privacy from './pages/Privacy';
import CoachBulkPost from './pages/CoachBulkPost';
import ManagerPortal from './pages/ManagerPortal';
import LeaguePortal from './pages/LeaguePortal';
import LeaguePage   from './pages/LeaguePage';
import AITools from './pages/AITools';
import Account from './pages/Account';
import EditStats from './pages/EditStats';
import PublicWatch from './pages/PublicWatch';
import NLCupWatch   from './pages/NLCupWatch';
import WVT2032Watch from './pages/WVT2032Watch';
import USYSWatch    from './pages/USYSWatch';
import ChicagoCupWatch from './pages/ChicagoCupWatch';

const PrivateRoute = ({ children }: { children: React.ReactNode }) => {
  const { isAuthenticated } = useAuth();
  return isAuthenticated ? <>{children}</> : <Navigate to="/login" replace />;
};

const AdminRoute = ({ children }: { children: React.ReactNode }) => {
  const { isAuthenticated, isAdmin } = useAuth();
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  if (!isAdmin) return <Navigate to="/teams" replace />;
  return <>{children}</>;
};

const CoachRoute = ({ children }: { children: React.ReactNode }) => {
  const { isAuthenticated, isAdmin, isCoach } = useAuth();
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  if (!isAdmin && !isCoach) return <Navigate to="/teams" replace />;
  return <>{children}</>;
};

const ManagerRoute = ({ children }: { children: React.ReactNode }) => {
  const { isAuthenticated, isAdmin, isTeamManager } = useAuth();
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  if (!isAdmin && !isTeamManager) return <Navigate to="/teams" replace />;
  return <>{children}</>;
};

const LeagueManagerRoute = ({ children }: { children: React.ReactNode }) => {
  const { isAuthenticated, isAdmin, isLeagueManager } = useAuth();
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  if (!isAdmin && !isLeagueManager) return <Navigate to="/teams" replace />;
  return <>{children}</>;
};

const AppRoutes = () => {
  const { isAuthenticated } = useAuth();
  // Register for Apple Push Notifications on iOS (no-op in browser)
  usePushNotifications(isAuthenticated);
  return (
    <Routes>
      <Route path="/login"    element={isAuthenticated ? <Navigate to="/teams" replace /> : <Login />} />
      <Route path="/register" element={isAuthenticated ? <Navigate to="/teams" replace /> : <Register />} />
      <Route path="/"         element={<Navigate to="/teams" replace />} />
      <Route path="/games"    element={<PrivateRoute><Layout><Home /></Layout></PrivateRoute>} />
      <Route path="/watch/:gameId" element={<PrivateRoute><Layout><Watch /></Layout></PrivateRoute>} />
      <Route path="/teams"         element={<PrivateRoute><Layout><Teams /></Layout></PrivateRoute>} />
      <Route path="/teams/:teamId" element={<PrivateRoute><Layout><TeamPage /></Layout></PrivateRoute>} />
      <Route path="/teams/:teamId/edit-stats" element={<ManagerRoute><Layout><EditStats /></Layout></ManagerRoute>} />
      <Route path="/players/:playerId" element={<PrivateRoute><Layout><PlayerProfile /></Layout></PrivateRoute>} />
      <Route path="/coach"   element={<CoachRoute><Layout><CoachBulkPost /></Layout></CoachRoute>} />
      <Route path="/admin"   element={<AdminRoute><Layout><Admin /></Layout></AdminRoute>} />
      <Route path="/manager"   element={<ManagerRoute><Layout><ManagerPortal /></Layout></ManagerRoute>} />
      <Route path="/league-portal" element={<LeagueManagerRoute><Layout><LeaguePortal /></Layout></LeagueManagerRoute>} />
      <Route path="/leagues/:id"   element={<Layout><LeaguePage /></Layout>} />
      <Route path="/ai-tools" element={<ManagerRoute><Layout><AITools /></Layout></ManagerRoute>} />
      <Route path="/account" element={<PrivateRoute><Layout><Account /></Layout></PrivateRoute>} />
      {/* ── Public pages — no login required ── */}
      <Route path="/NLCUP"         element={<NLCupWatch />} />
      <Route path="/WVT2032"       element={<WVT2032Watch />} />
      <Route path="/USYS"          element={<USYSWatch />} />
      <Route path="/chicago"       element={<ChicagoCupWatch />} />
      <Route path="/CHICAGO"       element={<ChicagoCupWatch />} />
      {/* Static slugs MUST come before the dynamic :gameId route */}
      <Route path="/live/:gameId" element={<PublicWatch />} />
      <Route path="/terms"   element={<Layout><Terms /></Layout>} />
      <Route path="/privacy" element={<Layout><Privacy /></Layout>} />
      <Route path="*" element={<Navigate to="/teams" replace />} />
    </Routes>
  );
};

const App = () => (
  <AuthProvider>
    <BrowserRouter>
      <AppRoutes />
    </BrowserRouter>
  </AuthProvider>
);

export default App;
